/**
 * Editable inputs that drive every calculator in the detail view.
 *
 * Layout mirrors find-properties.html: 4-input headline grid (Purchase /
 * Repair / ARV / Rent), then Closing Cost row, Section 8 row, collapsible
 * MAO panel, collapsible Financing panel. MAO + Financing use Collapsible so
 * they ship the "chevron-down to expand" affordance from the mockup.
 *
 * The component is fully controlled — parent owns state and decides when
 * to persist via `useDealSettings`.
 */
import { useState } from "react";
import { addMonths, format, parseISO } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  Calculator,
  CalendarClock,
  ChevronDown,
  List,
  Lock,
  Target,
  Wallet,
} from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  amortizedEmi,
  closingCost as closingCostFn,
  fmtMoney,
  interestOnlyMonthly,
  loanAmount as loanAmountFn,
  mao as maoFn,
  monthsToYearsMonths,
  originationFee,
  round2,
  termInMonths,
} from "@/lib/calculators";
import { TermField, type TermUnit } from "@/components/financing/TermField";
import type { LoanInputs, Strategy } from "@/types/pipeline";

import { InfoTip } from "./InfoTip";
import { applyMaoPct, toggleMaoAsPurchase } from "./detail-helpers";
import type { DealInputsState } from "./detail-helpers";

interface DealInputsProps {
  state: DealInputsState;
  onChange: (patch: Partial<DealInputsState>) => void;
  /** Active strategy — selects which loan input set (Flip/BRRRR HM or Rental long-term) is shown + edited. */
  strategy: Strategy;
  onStrategyChange: (strategy: Strategy) => void;
  /**
   * Hide the in-panel BRRRR/Flip/Rental strategy bar. Set in pipeline mode,
   * where the top Pipeline Strategy Bar dropdown is the single source of truth
   * for strategy selection (mirrors the design's `finStrategyBar` hide).
   */
  hideStrategyBar?: boolean;
  /**
   * Properties (owned) detail view. Hides the acquisition-only MAO panel, keeps
   * the Financing panel always expanded, and renders the per-loan Loan Date +
   * Maturity tracker (mirrors the design's `fromProperties` finance section).
   */
  fromProperties?: boolean;
  /** Fixed FMR rent estimate from external data (Section 8). When toggled on, rent is locked to this value. */
  section8FmrEstimate?: number;
  /** Original listed price (snapshot value before user edits). Used to render the delta chip. */
  originalPurchase?: number;
  /** Original ARV (snapshot-derived). Used to render the delta chip. */
  originalArv?: number;
  /** Click handler for "View breakdown" link beside Repair Cost (jumps to Rehab tab). */
  onViewRehabBreakdown?: () => void;
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Active loan accessor — reads/writes the right state slice based on strategy.
 * ────────────────────────────────────────────────────────────────────────── */

function getActiveLoan(state: DealInputsState, strategy: Strategy): LoanInputs {
  if (strategy === "Rental") {
    return (
      state.rental.long_term ?? { ltv: 80, rate: 7, term: 30, points: 1 }
    );
  }
  if (strategy === "Flip") {
    return (
      state.flip.hard_money ?? { ltv: 85, rate: 11, term: 12, points: 2 }
    );
  }
  return state.brrrr.hard_money ?? { ltv: 85, rate: 11, term: 12, points: 2 };
}

function setActiveLoan(
  state: DealInputsState,
  strategy: Strategy,
  patch: Partial<LoanInputs>,
): Partial<DealInputsState> {
  if (strategy === "Rental") {
    return {
      rental: {
        ...state.rental,
        long_term: { ...(state.rental.long_term ?? getActiveLoan(state, strategy)), ...patch },
      },
    };
  }
  if (strategy === "Flip") {
    return {
      flip: {
        ...state.flip,
        hard_money: { ...(state.flip.hard_money ?? getActiveLoan(state, strategy)), ...patch },
      },
    };
  }
  return {
    brrrr: {
      ...state.brrrr,
      hard_money: { ...(state.brrrr.hard_money ?? getActiveLoan(state, strategy)), ...patch },
    },
  };
}

/**
 * Build a term patch from the TermField's canonical months + chosen unit.
 * `term_months` is the source of truth; `term_unit` remembers the entry unit;
 * the legacy `term` stays in the slot-canonical unit (months for hard money,
 * years for long-term) for back-compat with any un-migrated reader.
 */
function termPatch(
  months: number,
  unit: TermUnit,
  slotDefault: TermUnit,
): Partial<LoanInputs> {
  const legacy =
    slotDefault === "years" ? Math.max(1, Math.round(months / 12)) : months;
  return { term_months: months, term_unit: unit, term: legacy };
}

/** Compact term readout for the "Total Loan Cost (…)" tile label. */
function fmtTermLabel(months: number): string {
  const { years, months: mo } = monthsToYearsMonths(months);
  if (years && mo) return `${years}y ${mo}m`;
  if (years) return `${years}yr`;
  return `${mo}mo`;
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Component
 * ────────────────────────────────────────────────────────────────────────── */

const MAO_PRESETS = [65, 70, 75] as const;

export function DealInputs({
  state,
  onChange,
  strategy,
  onStrategyChange,
  hideStrategyBar,
  fromProperties,
  section8FmrEstimate,
  originalPurchase,
  originalArv,
  onViewRehabBreakdown,
}: DealInputsProps) {
  const loan = getActiveLoan(state, strategy);
  const isShortTerm = strategy !== "Rental";
  // Owned BRRRR properties get a Short-Term/Long-Term selector that shows one
  // financing step at a time (mirrors the design's Properties-only
  // `finTypeSelect`). Everywhere else BRRRR shows both steps stacked.
  const loanView = state.loanView ?? "short";
  const ownedBrrrrSelector = Boolean(fromProperties && strategy === "BRRRR");
  const showStep1 = !ownedBrrrrSelector || loanView === "short";
  const showStep2 = !ownedBrrrrSelector || loanView === "long";
  const setLoanView = (view: "short" | "long") => onChange({ loanView: view });
  const closingDollar = closingCostFn(state.purchase, state.closingPct);
  const maoValue = maoFn(state.arv, state.rehab, state.mao.pct);
  const maoVsPurchase = maoValue - state.purchase;
  const underMao = maoVsPurchase >= 0;
  const usingCustomMao = !MAO_PRESETS.includes(state.mao.pct as 65 | 70 | 75);
  // Local text buffer for the custom-% input so it can be cleared/retyped
  // mid-edit. null = not editing → input mirrors the model.
  const [customMaoDraft, setCustomMaoDraft] = useState<string | null>(null);
  const [maoOpen, setMaoOpen] = useState(false);
  const [financingOpen, setFinancingOpen] = useState(false);
  const [moreDetailsOpen, setMoreDetailsOpen] = useState(false);
  // Loan input mode (LTV % vs fixed $ amount) — UI-only; the model always
  // stores LTV. Each loan slot keeps its own toggle (Step 1 → Purchase Price,
  // Step 2 refi → ARV).
  const [step1LoanMode, setStep1LoanMode] = useState<"pct" | "amt">("pct");
  const [step2LoanMode, setStep2LoanMode] = useState<"pct" | "amt">("pct");
  // Origination input mode (points vs fixed $ fee) — same deal: UI-only, the
  // model always stores points, and each loan slot keeps its own toggle.
  const [step1PointsMode, setStep1PointsMode] = useState<"pct" | "amt">("pct");
  const [step2PointsMode, setStep2PointsMode] = useState<"pct" | "amt">("pct");

  // ── Step 1 derived values (current strategy's primary loan) ──
  const financingEnabled = state.financingEnabled ?? true;
  // Hard money (Flip/BRRRR Step 1) terms default to months; Rental long-term to years.
  const loanSlotDefault: TermUnit = isShortTerm ? "months" : "years";
  const termMonths = termInMonths(loan, loanSlotDefault);
  // Principal the loan inputs describe. The *results* zero out when financing is
  // off, but the origination $/pts conversion still needs the real principal so
  // the field keeps working while the panel is toggled off.
  const step1LoanBase = loanAmountFn(state.purchase, loan.ltv);
  const step1LoanAmt = financingEnabled ? step1LoanBase : 0;
  const step1OrigFee = financingEnabled ? originationFee(step1LoanAmt, loan.points) : 0;
  const step1MonthlyInt = financingEnabled ? interestOnlyMonthly(step1LoanAmt, loan.rate) : 0;
  // The headline payment tile has to name the loan type it is showing. Hard
  // money (Flip / BRRRR Step 1) is interest-only and balloons the principal, so
  // "EMI" is the wrong word AND the wrong number there — an amortizing payment
  // retires the whole principal over the term, which contradicts the Balloon
  // Payment and Total Interest tiles directly below it. The long-term
  // (conventional / Rental) slot genuinely amortizes, so it keeps the EMI.
  const step1Payment = !financingEnabled
    ? 0
    : isShortTerm
      ? step1MonthlyInt
      : amortizedEmi(step1LoanAmt, loan.rate, termMonths);
  const step1PaymentLabel = isShortTerm
    ? "Monthly Payment (Interest-Only)"
    : "Monthly EMI";
  const step1TotalInt = step1MonthlyInt * termMonths;
  const step1TotalLoanCost = step1OrigFee + step1TotalInt;
  const step1CashNeeded =
    state.purchase + state.rehab + closingDollar + step1OrigFee - step1LoanAmt;
  // Hard money is interest-only → full principal due at term end. Long-term fully amortizes.
  const step1Balloon = isShortTerm ? step1LoanAmt : 0;

  // ── Step 2 (BRRRR-only refinance against ARV) ──
  const refiLoan = state.brrrr.long_term ?? { ltv: 75, rate: 7, term: 30, points: 1 };
  const step2TermMonths = termInMonths(refiLoan, "years");

  // Active loan for the top-of-section Loan Date + Maturity tracker — follows
  // the Short-Term/Long-Term selector (BRRRR long → refi loan; else Step 1 loan).
  const loanDateUsesRefi = ownedBrrrrSelector && loanView === "long";
  const loanDateLoan = loanDateUsesRefi ? refiLoan : loan;
  const loanDateTermMonths = loanDateUsesRefi ? step2TermMonths : termMonths;
  const step2LoanAmt = state.arv * (refiLoan.ltv / 100);
  const step2OrigFee = originationFee(step2LoanAmt, refiLoan.points);
  const step2Emi = amortizedEmi(step2LoanAmt, refiLoan.rate, step2TermMonths);
  const shortTermFull = termInMonths(state.brrrr.hard_money ?? loan, "months");
  const shortLoanForPayoff = state.purchase * ((state.brrrr.hard_money?.ltv ?? loan.ltv) / 100);
  const shortMonthlyIntForPayoff =
    shortLoanForPayoff * ((state.brrrr.hard_money?.rate ?? loan.rate) / 100 / 12);
  const payoffMonths = Math.max(
    0,
    Math.min(state.brrrr.hold_months ?? 6, shortTermFull),
  );
  const step2HoldingInterest = shortMonthlyIntForPayoff * payoffMonths;
  const step2SavedInterest =
    shortMonthlyIntForPayoff * Math.max(0, shortTermFull - payoffMonths);
  const step2NetCashOut =
    step2LoanAmt - shortLoanForPayoff - step2OrigFee - step2HoldingInterest;

  // Strategy-aware Step 1 label (mirrors applyStrategy() in find-properties.html)
  const step1Label =
    strategy === "BRRRR"
      ? "Step 1: Short-Term (Hard Money) — Acquisition"
      : strategy === "Flip"
        ? "Short-Term (Hard Money) Loan"
        : "Long-Term (Conventional) Loan";

  function updatePayoffMonths(v: number) {
    onChange({
      brrrr: { ...state.brrrr, hold_months: Math.max(1, Math.min(36, v || 1)) },
    });
  }
  function updateRefiLoan(patch: Partial<LoanInputs>) {
    onChange({
      brrrr: {
        ...state.brrrr,
        long_term: { ...refiLoan, ...patch },
      },
    });
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <header className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Calculator className="h-4 w-4 text-primary" aria-hidden />
        Deal Inputs
        <span className="ml-auto text-xs font-medium text-muted-foreground">
          Editable — recalculates live
        </span>
      </header>

      {/* Headline 4-input grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <NumberField
          label="Purchase Price"
          value={state.purchase}
          onChange={(v) => onChange({ purchase: v })}
          prefix="$"
          delta={
            originalPurchase != null
              ? state.purchase - originalPurchase
              : undefined
          }
          deltaBase={originalPurchase}
        />
        <NumberField
          label="Repair Cost"
          value={round2(state.rehab)}
          onChange={(v) => onChange({ rehab: v })}
          prefix="$"
          rightAdornment={
            onViewRehabBreakdown ? (
              <button
                type="button"
                onClick={onViewRehabBreakdown}
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold text-primary transition hover:bg-primary/10"
                aria-label="View rehab line-item breakdown"
              >
                <List className="h-3 w-3" aria-hidden />
                View breakdown
              </button>
            ) : null
          }
        />
        <NumberField
          label="After Repair Value"
          value={state.arv}
          onChange={(v) => onChange({ arv: v })}
          prefix="$"
          delta={
            originalArv != null ? state.arv - originalArv : undefined
          }
          deltaBase={originalArv}
        />
        <NumberField
          label="Monthly Rent"
          value={state.rent}
          onChange={(v) => onChange({ rent: v })}
          prefix="$"
          locked={state.useSection8Rent}
          lockedHint={state.useSection8Rent ? "Section 8" : undefined}
        />
      </div>

      {/* Closing Cost row */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed border-border bg-muted/40 p-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-bold">
            Closing Costs
            <InfoTip text="One-time costs at purchase: title, escrow, recording, lender, transfer tax, etc. Enter as a percent of Purchase Price (default 3%)." />
          </div>
          <div className="mt-1 text-base font-extrabold">
            {fmtMoney(Math.round(closingDollar))}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {state.closingPct.toFixed(2)}% × {fmtMoney(state.purchase)} (Purchase Price)
            {!state.closingAuto && " · custom"}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <PercentField
            value={state.closingPct}
            onChange={(v) =>
              onChange({ closingPct: v, closingAuto: false })
            }
          />
          <button
            type="button"
            onClick={() => onChange({ closingPct: 3, closingAuto: true })}
            className={cn(
              "rounded border px-2 py-0.5 text-[10px] font-semibold tracking-wide transition",
              state.closingAuto
                ? "cursor-default border-success/40 text-success"
                : "border-border text-muted-foreground hover:border-primary hover:text-primary",
            )}
            aria-label="Reset closing costs to 3%"
          >
            {state.closingAuto ? "Auto (3%)" : "Reset to 3%"}
          </button>
        </div>
      </div>

      {/* Section 8 row */}
      {section8FmrEstimate ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed border-border bg-muted/40 p-3">
          <div>
            <div className="flex items-center gap-1.5 text-xs font-bold">
              Section 8 Rent Estimate (FMR)
              <InfoTip text="HUD-estimated rent value used for Section 8 voucher programs in this area" />
              <Lock
                className="h-3 w-3 text-muted-foreground"
                aria-label="Fixed value"
              />
            </div>
            <div className="mt-1 text-base font-extrabold">
              {fmtMoney(section8FmrEstimate)}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <label className="inline-flex items-center gap-2 text-xs font-semibold">
              <Switch
                checked={state.useSection8Rent}
                onCheckedChange={(v) =>
                  onChange({
                    useSection8Rent: v,
                    rent: v ? section8FmrEstimate : state.rent,
                  })
                }
                aria-label="Use Section 8 rent as Monthly Rent"
              />
              <span className={cn(state.useSection8Rent && "text-primary")}>
                Use as Monthly Rent
              </span>
            </label>
            <div className="text-[11px] text-muted-foreground">
              vs Market Rent:{" "}
              <strong
                className={
                  section8FmrEstimate >= state.rent
                    ? "text-success"
                    : "text-destructive"
                }
              >
                {section8FmrEstimate >= state.rent ? "+" : "−"}
                {fmtMoney(Math.abs(section8FmrEstimate - state.rent))}
              </strong>
            </div>
          </div>
        </div>
      ) : null}

      {/* MAO panel — acquisition tool; hidden in the Properties (owned) view. */}
      {!fromProperties && (
      <Collapsible
        open={maoOpen}
        onOpenChange={setMaoOpen}
        className="mt-4 rounded-md border border-border bg-gradient-to-br from-success/[0.04] to-destructive/[0.04] p-3"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CollapsibleTrigger
            asChild
            className="cursor-pointer select-none"
          >
            <div className="inline-flex flex-1 items-center gap-1.5 text-sm font-bold">
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 text-muted-foreground transition-transform",
                  !maoOpen && "-rotate-90",
                )}
                aria-hidden
              />
              <Target className="h-4 w-4 text-primary" aria-hidden />
              Max Allowable Offer (MAO)
              <InfoTip text="The most you should offer to leave room for profit and risk. (ARV × %) − Rehab Cost. Lower % = more conservative." />
            </div>
          </CollapsibleTrigger>
          <div
            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 p-0.5"
            role="radiogroup"
            aria-label="MAO percent preset"
          >
            {MAO_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={state.mao.pct === p}
                onClick={() => {
                  setCustomMaoDraft(null);
                  onChange(applyMaoPct(state, p));
                }}
                className={cn(
                  "rounded-full px-2.5 py-1 text-[11px] font-bold transition",
                  state.mao.pct === p && !usingCustomMao
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {p}%
              </button>
            ))}
            <input
              type="number"
              min={1}
              max={99}
              value={customMaoDraft ?? (usingCustomMao ? state.mao.pct : "")}
              placeholder="Custom"
              onChange={(e) => {
                const text = e.target.value;
                setCustomMaoDraft(text);
                // Empty / invalid stays in the buffer only — model is untouched
                // so the user can clear the field and retype.
                if (text === "") return;
                const raw = Number(text);
                if (!Number.isFinite(raw) || raw <= 0) return;
                onChange(applyMaoPct(state, Math.max(1, Math.min(99, raw))));
              }}
              onBlur={() => setCustomMaoDraft(null)}
              className={cn(
                "w-16 rounded-full bg-transparent px-2 py-1 text-center text-[11px] font-bold outline-none",
                usingCustomMao
                  ? "bg-primary text-primary-foreground placeholder:text-primary-foreground/70"
                  : "text-muted-foreground placeholder:text-muted-foreground/70",
              )}
              aria-label="Custom MAO percent"
            />
          </div>
        </div>
        <CollapsibleContent>
          <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-[11px] text-muted-foreground">
                (ARV ×{" "}
                <span className="font-semibold text-foreground">
                  {state.mao.pct}%
                </span>
                ) − Rehab Cost
              </div>
              <div className="text-2xl font-extrabold leading-tight">
                {fmtMoney(Math.round(maoValue))}
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-xs font-semibold">
              <Switch
                checked={state.mao.use_as_purchase}
                onCheckedChange={(v) => onChange(toggleMaoAsPurchase(state, v))}
                aria-label="Use MAO as purchase price"
              />
              <span className={cn(state.mao.use_as_purchase && "text-primary")}>
                Use as Purchase Price
              </span>
            </label>
          </div>
          <div
            className={cn(
              "mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-xs",
              underMao
                ? "border-success/30 bg-success/5"
                : "border-destructive/30 bg-destructive/5",
            )}
          >
            <span>
              vs Purchase Price:{" "}
              <strong
                className={cn(
                  "font-bold",
                  underMao ? "text-success" : "text-destructive",
                )}
              >
                {underMao ? "+" : "−"}
                {fmtMoney(Math.abs(maoVsPurchase))}
              </strong>
            </span>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[11px] font-bold",
                underMao
                  ? "bg-success/15 text-success"
                  : "bg-destructive/15 text-destructive",
              )}
            >
              {underMao ? "Under MAO — good deal" : "Over MAO — paying too much"}
            </span>
          </div>
        </CollapsibleContent>
      </Collapsible>
      )}

      {/* Financing panel — always expanded in the Properties (owned) view. */}
      <Collapsible
        open={fromProperties || financingOpen}
        onOpenChange={setFinancingOpen}
        className="mt-4 rounded-md border border-border bg-muted/40 p-3"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CollapsibleTrigger
            asChild
            disabled={fromProperties}
            className={cn(!fromProperties && "cursor-pointer select-none")}
          >
            <div className="inline-flex flex-1 items-center gap-1.5 text-sm font-bold">
              {!fromProperties && (
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 text-muted-foreground transition-transform",
                    !financingOpen && "-rotate-90",
                  )}
                  aria-hidden
                />
              )}
              <Wallet className="h-4 w-4 text-primary" aria-hidden />
              Financing
              <InfoTip text="Choose between short-term hard money (interest-only with balloon) or long-term conventional financing (fully amortizing)." />
            </div>
          </CollapsibleTrigger>
          <div className="inline-flex items-center gap-3">
            {/* Short-Term / Long-Term selector — owned BRRRR properties only.
                Picks which loan (Step 1 hard money vs Step 2 refi) is active. */}
            {ownedBrrrrSelector && (
              <div
                className="inline-flex items-center gap-0.5 rounded-full border border-border bg-card p-0.5"
                role="radiogroup"
                aria-label="Active loan type"
                title="Which loan type is active for this property"
              >
                {(
                  [
                    ["short", "Short-Term"],
                    ["long", "Long-Term"],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={loanView === key}
                    onClick={() => setLoanView(key)}
                    className={cn(
                      "rounded-full px-2.5 py-1 text-[11px] font-bold transition",
                      loanView === key
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <label
              className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground"
              onClick={(e) => e.stopPropagation()}
            >
            <Switch
              checked={state.financingEnabled ?? true}
              onCheckedChange={(v) => onChange({ financingEnabled: v })}
              aria-label="Enable financing"
            />
            <span
              className={cn(
                (state.financingEnabled ?? true) && "text-primary",
              )}
            >
              Enabled
            </span>
            </label>
          </div>
        </div>

        <CollapsibleContent>
          {/* Strategy bar — hidden in pipeline mode, where the top Pipeline
              Strategy Bar dropdown owns strategy selection (single source of
              truth, matching the design's `finStrategyBar` hide). */}
          {!hideStrategyBar && (
            <div
              className="mt-2 inline-flex w-full gap-0.5 rounded-full border border-border bg-card p-1"
              role="radiogroup"
              aria-label="Investment strategy"
            >
              {(["BRRRR", "Flip", "Rental"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={strategy === s}
                  onClick={() => onStrategyChange(s)}
                  className={cn(
                    "flex-1 rounded-full px-3 py-1.5 text-[11px] font-bold transition",
                    strategy === s
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Loan Date + Maturity — Properties (owned) view only. Sits at the
              top of the financing section and tracks the active loan (the one
              selected by the Short-Term/Long-Term pill for BRRRR). */}
          {fromProperties && (
            <LoanDateField
              value={loanDateLoan.loan_date}
              termMonths={loanDateTermMonths}
              onChange={(v) =>
                loanDateUsesRefi
                  ? updateRefiLoan({ loan_date: v })
                  : onChange(setActiveLoan(state, strategy, { loan_date: v }))
              }
            />
          )}

          {/* ── Step 1: loan inputs ── (hidden in owned-BRRRR Long-Term view) */}
          {showStep1 && (
          <>
          <div className="mt-3 text-[11px] font-bold uppercase tracking-wider text-primary">
            {strategy === "BRRRR" && (
              <span className="mr-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-primary text-[9px]">
                1
              </span>
            )}
            {step1Label}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <LoanField
              ltv={loan.ltv}
              base={state.purchase}
              mode={step1LoanMode}
              onModeChange={setStep1LoanMode}
              onLtvChange={(v) =>
                onChange(setActiveLoan(state, strategy, { ltv: v }))
              }
              tip="Lender funds this share of the Purchase Price. Enter it as an LTV % or as a fixed loan amount — the other updates automatically. Typical LTV 70–90%."
            />
            <SmallNumberField
              label="Interest Rate"
              value={loan.rate}
              step={0.25}
              onChange={(v) =>
                onChange(setActiveLoan(state, strategy, { rate: v }))
              }
              suffix="% p.a."
              tip="Annual interest rate (per annum). Monthly interest is calculated as Loan × (Rate ÷ 12)."
            />
            <div className="flex flex-col gap-1 sm:col-span-2">
              <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
                Term
                <InfoTip text="Loan term. Hard money is usually months; long-term financing is years — switch the unit to enter a mixed term like 30 years 6 months." />
              </label>
              <TermField
                months={termMonths}
                unit={loan.term_unit ?? loanSlotDefault}
                onChange={({ months, unit }) =>
                  onChange(
                    setActiveLoan(
                      state,
                      strategy,
                      termPatch(months, unit, loanSlotDefault),
                    ),
                  )
                }
                inputClassName="h-8"
                triggerClassName="h-8"
              />
            </div>
            <OriginationField
              points={loan.points}
              loanAmt={step1LoanBase}
              mode={step1PointsMode}
              onModeChange={setStep1PointsMode}
              onPointsChange={(v) =>
                onChange(setActiveLoan(state, strategy, { points: v }))
              }
            />
          </div>

          {/* ── Step 1 results ── */}
          {financingEnabled ? (
            <>
              <div className="mt-3 grid grid-cols-2 gap-3 border-t border-dashed border-border pt-3 sm:grid-cols-4">
                <ResultTile label="Loan Amount" value={fmtMoney(step1LoanAmt)} highlight />
                <ResultTile
                  label={step1PaymentLabel}
                  value={fmtMoney(step1Payment)}
                />
                <ResultTile
                  label="Cash Needed"
                  value={fmtMoney(step1CashNeeded)}
                  tone="cash"
                />
                <ResultTile label="Origination Fee" value={fmtMoney(step1OrigFee)} />
              </div>

              <Collapsible
                open={moreDetailsOpen}
                onOpenChange={setMoreDetailsOpen}
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="mt-2 inline-flex items-center gap-1 text-[12px] font-bold text-primary hover:brightness-110"
                  >
                    {moreDetailsOpen ? "Hide details" : "More details"}
                    <ChevronDown
                      className={cn(
                        "h-3.5 w-3.5 transition-transform",
                        moreDetailsOpen && "rotate-180",
                      )}
                      aria-hidden
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {/* Interest-only slots already show this as the headline
                        payment; only the amortizing slot has a distinct
                        interest portion worth breaking out. */}
                    {!isShortTerm && (
                      <ResultTile
                        label="Monthly Interest"
                        value={fmtMoney(step1MonthlyInt)}
                      />
                    )}
                    <ResultTile
                      label={`Total Loan Cost (${fmtTermLabel(termMonths)})`}
                      value={fmtMoney(step1TotalLoanCost)}
                    />
                    <ResultTile label="Total Interest" value={fmtMoney(step1TotalInt)} />
                    <ResultTile label="Balloon Payment" value={fmtMoney(step1Balloon)} />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </>
          ) : (
            <div className="mt-3 border-t border-dashed border-border pt-3 text-center text-xs text-muted-foreground">
              Financing disabled — buyer paying all cash
            </div>
          )}
          </>
          )}

          {/* ── Step 2 (BRRRR only): long-term refinance ──
              (hidden in owned-BRRRR Short-Term view) */}
          {strategy === "BRRRR" && financingEnabled && showStep2 && (
            <div className="mt-4 border-t border-border pt-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-primary">
                <span className="mr-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-primary text-[9px]">
                  2
                </span>
                Step 2: Long-Term — Refinance (against ARV)
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <LoanField
                  ltv={refiLoan.ltv}
                  base={state.arv}
                  mode={step2LoanMode}
                  onModeChange={setStep2LoanMode}
                  onLtvChange={(v) => updateRefiLoan({ ltv: v })}
                  tip="Refinance loan against the After-Repair Value (ARV). Enter it as an LTV % or as a fixed loan amount — the other updates automatically. Typical LTV 70–80%."
                />
                <SmallNumberField
                  label="Interest Rate"
                  value={refiLoan.rate}
                  step={0.25}
                  onChange={(v) => updateRefiLoan({ rate: v })}
                  suffix="% p.a."
                  tip="Annual interest rate (per annum). Monthly interest is calculated as Loan × (Rate ÷ 12)."
                />
                <div className="flex flex-col gap-1 sm:col-span-2">
                  <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
                    Term
                    <InfoTip text="Refinance term. Defaults to years — switch the unit to enter a mixed term like 30 years 6 months." />
                  </label>
                  <TermField
                    months={step2TermMonths}
                    unit={refiLoan.term_unit ?? "years"}
                    onChange={({ months, unit }) =>
                      updateRefiLoan(termPatch(months, unit, "years"))
                    }
                    inputClassName="h-8"
                    triggerClassName="h-8"
                  />
                </div>
                <OriginationField
                  points={refiLoan.points}
                  loanAmt={step2LoanAmt}
                  mode={step2PointsMode}
                  onModeChange={setStep2PointsMode}
                  onPointsChange={(v) => updateRefiLoan({ points: v })}
                />
              </div>

              {/* Early-payoff row */}
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-card px-3 py-2 text-[12px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 font-semibold text-primary">
                  ⏵ Close Step 1 loan early after
                </span>
                <NumberInput
                  min={1}
                  value={payoffMonths}
                  parse={(raw) => Math.max(1, Number(raw) || 1)}
                  onChange={updatePayoffMonths}
                  className="h-7 w-16 border-border bg-muted/40 px-2 text-right text-xs font-bold focus-visible:ring-0 focus-visible:ring-offset-0"
                  aria-label="Payoff months"
                />
                <span>mo</span>
                {step2SavedInterest > 0 ? (
                  <span>
                    <span className="font-bold text-success">
                      saves {fmtMoney(step2SavedInterest)}
                    </span>{" "}
                    vs full {shortTermFull}-mo term
                  </span>
                ) : (
                  <span className="text-muted-foreground/70">
                    (full {shortTermFull}-mo term)
                  </span>
                )}
              </div>

              {/* Step 2 results */}
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <ResultTile label="Refi Loan" value={fmtMoney(step2LoanAmt)} highlight />
                <ResultTile label="Monthly EMI" value={fmtMoney(step2Emi)} />
                <ResultTile
                  label="Net Cash Out"
                  value={
                    (step2NetCashOut < 0 ? "−" : "") +
                    fmtMoney(Math.abs(step2NetCashOut))
                  }
                  tone={step2NetCashOut >= 0 ? "cash" : "danger"}
                />
                <ResultTile label="Origination Fee" value={fmtMoney(step2OrigFee)} />
                <ResultTileWide
                  label={`Holding Interest (${payoffMonths} mo × ${fmtMoney(shortMonthlyIntForPayoff)})`}
                  value={fmtMoney(step2HoldingInterest)}
                  tone="danger"
                />
                <ResultTileWide
                  label="Step 1 Principal Paid Off"
                  value={fmtMoney(shortLoanForPayoff)}
                />
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Result tile primitives (Step 1 / Step 2 financing readouts)
 * ────────────────────────────────────────────────────────────────────────── */

function ResultTile({
  label,
  value,
  highlight,
  tone,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  tone?: "cash" | "danger";
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-[11px] font-semibold text-muted-foreground">{label}</div>
      <div
        className={cn(
          "text-base font-extrabold leading-tight",
          highlight && "text-primary",
          tone === "cash" && "text-success",
          tone === "danger" && "text-destructive",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function ResultTileWide({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "cash" | "danger";
}) {
  return (
    <div className="flex flex-col gap-0.5 sm:col-span-2">
      <div className="text-[11px] font-semibold text-muted-foreground">{label}</div>
      <div
        className={cn(
          "text-base font-extrabold leading-tight",
          tone === "cash" && "text-success",
          tone === "danger" && "text-destructive",
        )}
      >
        {value}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Field primitives
 * ────────────────────────────────────────────────────────────────────────── */

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
  locked?: boolean;
  lockedHint?: string;
  step?: number;
  /** Signed change from original; renders a +/− chip next to the label. */
  delta?: number;
  /** Original base for percent calculation in the delta chip. */
  deltaBase?: number;
  /** Right-side adornment in the label row (e.g. "View breakdown" link). */
  rightAdornment?: React.ReactNode;
}

function NumberField({
  label,
  value,
  onChange,
  prefix,
  suffix,
  locked,
  lockedHint,
  step,
  delta,
  deltaBase,
  rightAdornment,
}: NumberFieldProps) {
  const showDelta = delta !== undefined && delta !== 0;
  const deltaPct =
    showDelta && deltaBase ? (delta! / deltaBase) * 100 : 0;
  const isNegative = (delta ?? 0) < 0;

  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-center justify-between gap-1.5 text-[11px] font-semibold text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          {label}
          {showDelta && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 rounded-full px-1.5 py-[1px] text-[10px] font-bold",
                isNegative
                  ? "bg-success/15 text-success"
                  : "bg-destructive/15 text-destructive",
              )}
            >
              {isNegative ? (
                <ArrowDown className="h-2.5 w-2.5" aria-hidden />
              ) : (
                <ArrowUp className="h-2.5 w-2.5" aria-hidden />
              )}
              {isNegative ? "−" : "+"}
              {fmtMoney(Math.abs(delta!))} ({isNegative ? "−" : "+"}
              {Math.abs(deltaPct).toFixed(1)}%)
            </span>
          )}
          {locked && lockedHint && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-primary">
              · {lockedHint}
            </span>
          )}
        </span>
        {rightAdornment}
      </label>
      <div
        className={cn(
          "relative rounded-md border border-border bg-muted/40 transition focus-within:border-primary",
          locked && "border-dashed opacity-60",
        )}
      >
        {prefix && (
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            {prefix}
          </span>
        )}
        <NumberInput
          step={step}
          value={Number.isFinite(value) ? value : 0}
          readOnly={locked}
          onChange={onChange}
          className={cn(
            "h-9 w-full border-0 bg-transparent font-bold focus-visible:ring-0 focus-visible:ring-offset-0",
            prefix && "pl-6",
            suffix && "pr-9",
          )}
        />
        {suffix && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

interface PercentFieldProps {
  value: number;
  onChange: (v: number) => void;
}

function PercentField({ value, onChange }: PercentFieldProps) {
  return (
    <div className="relative w-24 rounded-md border border-border bg-muted/40 focus-within:border-primary">
      <NumberInput
        step={0.25}
        min={0}
        max={20}
        value={value}
        onChange={onChange}
        className="h-8 w-full border-0 bg-transparent pr-7 text-right font-bold focus-visible:ring-0 focus-visible:ring-offset-0"
      />
      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
        %
      </span>
    </div>
  );
}

interface SmallNumberFieldProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  step?: number;
  /** Help-tip text — renders an info icon beside the label when set. */
  tip?: string;
}

function SmallNumberField({
  label,
  value,
  onChange,
  suffix,
  step,
  tip,
}: SmallNumberFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
        {label}
        {tip && <InfoTip text={tip} />}
      </label>
      <div className="relative rounded-md border border-border bg-card focus-within:border-primary">
        <NumberInput
          step={step}
          value={Number.isFinite(value) ? value : 0}
          onChange={onChange}
          className={cn(
            "h-8 w-full border-0 bg-transparent font-bold focus-visible:ring-0 focus-visible:ring-offset-0",
            suffix && "pr-9",
          )}
        />
        {suffix && (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

/** Percent-or-dollar entry mode for the `.loan-mode-field` style inputs. */
type ValueMode = "pct" | "amt";

interface PercentAmountFieldProps {
  label: string;
  /** Stored percent — the canonical model value (LTV %, origination points). */
  pct: number;
  /** Base the dollar amount is a percentage of (Purchase Price, ARV, loan). */
  base: number;
  mode: ValueMode;
  onModeChange: (mode: ValueMode) => void;
  /** Always reports back a percent — $ entry is converted before this fires. */
  onPctChange: (pct: number) => void;
  /** Unit rendered inside the input in % mode ("%" / "pts"). */
  pctSuffix: string;
  pctMin: number;
  pctMax?: number;
  pctStep: number;
  amtStep: number;
  /** aria-labels for the two toggle buttons. */
  pctModeLabel: string;
  amtModeLabel: string;
  /** Help-tip text — renders an info icon beside the label when set. */
  tip?: string;
}

/**
 * Numeric field with a %/$ mode toggle (mirrors find-properties.html's
 * `.loan-mode-field`). The value is a percentage of `base` — enter it either
 * way and the other updates automatically. The model always stores the percent,
 * so `$` input is converted back on change; the conversion is rounded to 5
 * decimals (matching LoanEditDrawer) so a typed dollar figure round-trips
 * without drifting the derived readouts.
 */
function PercentAmountField({
  label,
  pct,
  base,
  mode,
  onModeChange,
  onPctChange,
  pctSuffix,
  pctMin,
  pctMax,
  pctStep,
  amtStep,
  pctModeLabel,
  amtModeLabel,
  tip,
}: PercentAmountFieldProps) {
  const safePct = Number.isFinite(pct) ? pct : 0;
  const amount = round2(base * (safePct / 100));
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-1">
        {/* A <span>, not a <label>: the input is named by aria-label, and a
            wrapping label would otherwise claim the info-tip button as the
            field's control. */}
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
          {label}
          {tip && <InfoTip text={tip} />}
        </span>
        <span className="inline-flex items-center gap-0.5 rounded-md border border-border bg-card p-0.5">
          {(["pct", "amt"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onModeChange(m)}
              className={cn(
                "rounded px-1.5 py-px text-[10px] font-bold leading-none transition-colors",
                mode === m
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-pressed={mode === m}
              aria-label={m === "pct" ? pctModeLabel : amtModeLabel}
            >
              {m === "pct" ? "%" : "$"}
            </button>
          ))}
        </span>
      </div>
      <div className="relative rounded-md border border-border bg-card focus-within:border-primary">
        {mode === "amt" && (
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
            $
          </span>
        )}
        <NumberInput
          // The label sits in its own row beside the toggle, so it isn't wired
          // to the input — name it explicitly instead.
          aria-label={label}
          min={mode === "pct" ? pctMin : 0}
          max={mode === "pct" ? pctMax : undefined}
          step={mode === "pct" ? pctStep : amtStep}
          value={mode === "pct" ? safePct : amount}
          onChange={(raw) => {
            if (mode === "pct") {
              onPctChange(raw);
            } else {
              onPctChange(
                base > 0
                  ? Math.round((raw / base) * 100 * 1e5) / 1e5
                  : safePct,
              );
            }
          }}
          className={cn(
            "h-8 w-full border-0 bg-transparent font-bold focus-visible:ring-0 focus-visible:ring-offset-0",
            mode === "amt" ? "pl-6" : "pr-9",
          )}
        />
        {mode === "pct" && (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
            {pctSuffix}
          </span>
        )}
      </div>
    </div>
  );
}

interface LoanFieldProps {
  /** Stored LTV percent — the canonical model value. */
  ltv: number;
  /** Base the dollar amount is computed against (Purchase Price or ARV). */
  base: number;
  /** Input mode: enter as LTV % or as a fixed $ amount. */
  mode: ValueMode;
  onModeChange: (mode: ValueMode) => void;
  /** Always reports back an LTV — the $ amount is converted before this fires. */
  onLtvChange: (ltv: number) => void;
  /** Help-tip text — renders an info icon beside the "Loan" label when set. */
  tip?: string;
}

/**
 * Loan amount input. The lender funds this share of the base value — enter it
 * as an LTV % or a fixed loan amount.
 */
function LoanField({ ltv, base, mode, onModeChange, onLtvChange, tip }: LoanFieldProps) {
  return (
    <PercentAmountField
      label="Loan"
      pct={ltv}
      base={base}
      mode={mode}
      onModeChange={onModeChange}
      onPctChange={onLtvChange}
      pctSuffix="%"
      pctMin={1}
      pctMax={100}
      pctStep={0.5}
      amtStep={1000}
      pctModeLabel="Enter as LTV percent"
      amtModeLabel="Enter as loan amount"
      tip={tip}
    />
  );
}

interface OriginationFieldProps {
  /** Stored origination points — the canonical model value (1 pt = 1% of loan). */
  points: number;
  /** Loan principal the fee is charged against. */
  loanAmt: number;
  /** Input mode: enter as points or as a fixed $ fee. */
  mode: ValueMode;
  onModeChange: (mode: ValueMode) => void;
  /** Always reports back points — the $ fee is converted before this fires. */
  onPointsChange: (points: number) => void;
}

/**
 * Origination input with the same %/$ toggle as the Loan field. The model
 * stores points, so a dollar fee is converted back on change — which means the
 * fee keeps tracking the loan amount if the LTV is edited afterwards.
 */
function OriginationField({
  points,
  loanAmt,
  mode,
  onModeChange,
  onPointsChange,
}: OriginationFieldProps) {
  return (
    <PercentAmountField
      label="Origination"
      pct={points}
      base={loanAmt}
      mode={mode}
      onModeChange={onModeChange}
      onPctChange={onPointsChange}
      pctSuffix="pts"
      pctMin={0}
      pctStep={0.25}
      amtStep={100}
      pctModeLabel="Enter as origination points"
      amtModeLabel="Enter as origination fee in dollars"
      tip="Up-front fee charged by the lender. Enter it as points (1 point = 1% of loan) or as a fixed dollar fee — the other updates automatically."
    />
  );
}

interface LoanDateFieldProps {
  /** Field label (e.g. "Loan Date" / "Refi Loan Date"). */
  label?: string;
  /** Persisted loan funding date as "YYYY-MM-DD" (or null/undefined when unset). */
  value?: string | null;
  /** Active loan term in months — maturity = loan date + this many months. */
  termMonths: number;
  onChange: (value: string) => void;
}

/**
 * Loan Date input + computed Maturity Date — Properties (owned) finance view
 * only. Maturity = loan date + the active loan's term (already normalised to
 * months by the caller). Dates are "YYYY-MM-DD" strings; parsed/formatted via
 * date-fns so no timezone rollback (see CLAUDE.md calendar rules).
 */
function LoanDateField({ label = "Loan Date", value, termMonths, onChange }: LoanDateFieldProps) {
  let maturity = "—";
  if (value && termMonths) {
    const parsed = parseISO(value);
    if (!Number.isNaN(parsed.getTime())) {
      maturity = format(addMonths(parsed, termMonths), "MMM d, yyyy");
    }
  }

  return (
    <div className="mt-3 flex flex-wrap gap-3 rounded-md border border-border bg-card p-3">
      <div className="flex min-w-[150px] flex-1 flex-col gap-1">
        <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
          <CalendarClock className="h-3 w-3" aria-hidden />
          {label}
          <InfoTip text="The date the loan funded. Maturity is calculated as loan date + the active loan term." />
        </label>
        <Input
          type="date"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-full border-border bg-muted/40 font-bold focus-visible:ring-0"
          aria-label={label}
        />
      </div>
      <div className="flex min-w-[150px] flex-1 flex-col gap-1">
        <span className="text-[11px] font-semibold text-muted-foreground">
          Maturity Date
        </span>
        <div className="px-0.5 py-1.5 text-sm font-extrabold tabular-nums">
          {maturity}
        </div>
      </div>
    </div>
  );
}
